import { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

interface ModalRepeatValuesProps {
  open: boolean;
  onClose: () => void;
  onApply: (count: number, value: string) => void;
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
};

export function ModalRepeatValues({ open, onClose, onApply }: ModalRepeatValuesProps) {
  const [count, setCount] = useState('');
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) {
      setCount('');
      setValue('');
    }
  }, [open]);

  const countNum = parseInt(count, 10);
  const isCountValid = !isNaN(countNum) && countNum > 0 && countNum <= 100;
  const canApply = isCountValid && value.trim() !== '';

  const preview = canApply
    ? `Will fill "${value}" across ${countNum} column${countNum !== 1 ? 's' : ''}`
    : null;

  const handleApply = () => {
    if (!canApply) return;
    onApply(countNum, value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canApply) handleApply();
    if (e.key === 'Escape') onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={modalContentStyle} spacing={2} onKeyDown={handleKeyDown}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797' }}>
            Repeat Values
          </Typography>
          <IconButton size='small' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
        <Divider />
        <Typography variant='body2' color='text.secondary'>
          Fill a value across consecutive columns starting from the selected cell.
        </Typography>
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <TextField
            label='Count'
            size='small'
            type='number'
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{ min: 1, max: 100 }}
            error={count !== '' && !isCountValid}
            helperText={count !== '' && !isCountValid ? 'Must be 1–100' : ' '}
            sx={{ width: 120 }}
            autoFocus
          />
          <TextField
            label='Value'
            size='small'
            value={value}
            onChange={(e) => setValue(e.target.value)}
            helperText=' '
            sx={{ flex: 1 }}
          />
        </Stack>
        <Typography
          variant='caption'
          sx={{ color: '#42BB93', fontStyle: 'italic', minHeight: 18 }}
        >
          {preview}
        </Typography>
        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
          <Button
            variant='main-table-panel-border'
            onClick={onClose}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant='main-table-panel'
            disabled={!canApply}
            onClick={handleApply}
            sx={{ textTransform: 'none' }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
